import { AuthErrorCode } from 'types/auth/helpers'

/**
 * Classify Apollo / fetch errors by cause.
 *
 * Used by SessionManager and OfflineSyncService to decide whether to
 * terminate the session (auth error) or defer and retry (network error).
 */

export const isNetworkError = (error: unknown): boolean => {
  if (!error) {
    return false
  }

  // Apollo wraps fetch failures as ApolloError with a non-null networkError
  const apolloError = error as any

  if (apolloError.networkError != null) {
    return true
  }

  // Plain fetch failures (TypeError thrown by the browser)
  const msg = (error as Error).message ?? ''

  if (
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('Load failed') || // Safari
    msg.includes('network request failed')
  ) {
    return true
  }

  // Belt-and-suspenders: browser already knows we are offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true
  }

  return false
}

const AUTH_ERROR_CODES = new Set([
  AuthErrorCode.UNAUTHENTICATED,
  AuthErrorCode.SESSION_EXPIRED,
  AuthErrorCode.SESSION_REVOKED,
  AuthErrorCode.SESSION_TOKEN_REUSE
])

export const isAuthError = (error: unknown): boolean => {
  if (!error) return false

  const apolloError = error as any
  const errors: any[] = apolloError?.graphQLErrors ?? apolloError?.errors ?? []

  return errors.some((e) => AUTH_ERROR_CODES.has(e?.extensions?.code))
}
